import type React from "react"
import { useState, useEffect } from "react"
import {
  Container,
  Typography,
  Box,
  Chip,
  Alert,
  AlertTitle,
  Card,
  CardContent,
} from "@mui/material"
import { useNavigate, useOutletContext } from "react-router-dom"
import PropertyFooterLayout from "../../../components/properties/PropertyFooterLayout"
import { 
  Handshake, 
  Share, 
  Percent, 
  Info,
  AccountBalance
} from "@mui/icons-material"

const progress = 15
const step = 3

type PublishOutletContext = {
  handleNavigateToPropertyPrice: () => void
  commission: string
  setCommission: (commission: string) => void
  commissionType: number
  setCommissionType: (type: number) => void
}

export const PropertyCommissionPage: React.FC = () => {
  const navigate = useNavigate()
  const { 
    handleNavigateToPropertyPrice, 
    commission, 
    setCommission, 
    commissionType, 
    setCommissionType 
  } = useOutletContext<PublishOutletContext>()

  const [localCommission, setLocalCommission] = useState<string>("")
  const [localCommissionType, setLocalCommissionType] = useState<number>(1)

  useEffect(() => {
    setLocalCommission(commission)
    setLocalCommissionType(commissionType)
  }, [commission, commissionType])

  const handleBack = () => {
    navigate(-1)
  }

  const handleContinue = () => {
    setCommission(localCommission)
    setCommissionType(localCommissionType)
    handleNavigateToPropertyPrice()
  }

  const handleCommissionTypeChange = (type: number) => {
    setLocalCommissionType(type)
  }

  const handleCommissionChange = (commissionValue: string) => {
    setLocalCommission(commissionValue)
  }

  const getCommissionTypeDescription = (type: number) => {
    switch (type) {
      case 1:
        return "Comisión directa: Haces un trato directo con el dueño de la propiedad para repartir el 100% de la comisión con otro broker."
      case 2:
        return "Comisión compartida: Un broker te está compartiendo su parte de la comisión que le fue asignada originalmente."
      default:
        return ""
    }
  }

  return (
    <Container maxWidth="md" sx={{ display: "flex", pb: 12, flexDirection: "column", alignItems: "center" }}>
      <Box p={4} sx={{ width: "80%" }}>
        {/* Header Section */}
        <Box textAlign="center" mb={5}>
          <Box 
            sx={{ 
              display: "inline-flex", 
              alignItems: "center", 
              justifyContent: "center",
              width: 80, 
              height: 80, 
              borderRadius: "50%", 
              background: "linear-gradient(135deg, #66bb6a, #4caf50)",
              mb: 2,
              boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)"
            }}
          >
            <AccountBalance sx={{ fontSize: 40, color: "white" }} />
          </Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", color: "#2e7d32" }}>
            Configurar Comisión
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 500, margin: "0 auto" }}>
            Define el tipo de comisión y los términos de distribución para esta propiedad.
          </Typography>
        </Box>

        {/* Tipo de Comisión */}
        <Card sx={{ mb: 4, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  width: 40, 
                  height: 40, 
                  borderRadius: "50%", 
                  background: "linear-gradient(135deg, #81c784, #66bb6a)",
                  mr: 2
                }}
              >
                <Handshake sx={{ fontSize: 24, color: "white" }} />
              </Box>
              <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: "bold" }}>
                Tipo de Comisión
              </Typography>
            </Box>
            
            <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap", justifyContent: "center" }}>
              <Chip
                icon={<Handshake />}
                label="Comisión Directa"
                clickable
                onClick={() => handleCommissionTypeChange(1)}
                sx={{
                  height: 48,
                  backgroundColor: localCommissionType === 1 
                    ? "transparent"
                    : "rgba(76, 175, 80, 0.08)",
                  background: localCommissionType === 1 
                    ? "linear-gradient(135deg, #66bb6a, #4caf50)" 
                    : "rgba(76, 175, 80, 0.08)",
                  color: localCommissionType === 1 ? "white" : "#2e7d32",
                  border: localCommissionType === 1 ? "none" : "1px solid #81c784",
                  fontWeight: localCommissionType === 1 ? "bold" : "medium",
                  fontSize: "0.95rem",
                  px: 2,
                  boxShadow: localCommissionType === 1 ? "0 4px 12px rgba(76, 175, 80, 0.3)" : "none",
                  transition: "all 0.3s ease",
                  "& .MuiChip-icon": {
                    color: localCommissionType === 1 ? "white" : "#4caf50"
                  },
                  "&:hover": {
                    transform: "translateY(-2px)",
                    background: localCommissionType === 1 
                      ? "linear-gradient(135deg, #5cb85c, #449d44)"
                      : "rgba(76, 175, 80, 0.15)",
                    boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)"
                  }
                }}
              />
              
              <Chip
                icon={<Share />}
                label="Comisión Compartida"
                clickable
                onClick={() => handleCommissionTypeChange(2)}
                sx={{
                  height: 48,
                  backgroundColor: localCommissionType === 2 
                    ? "transparent"
                    : "rgba(76, 175, 80, 0.08)",
                  background: localCommissionType === 2 
                    ? "linear-gradient(135deg, #66bb6a, #4caf50)" 
                    : "rgba(76, 175, 80, 0.08)",
                  color: localCommissionType === 2 ? "white" : "#2e7d32",
                  border: localCommissionType === 2 ? "none" : "1px solid #81c784",
                  fontWeight: localCommissionType === 2 ? "bold" : "medium",
                  fontSize: "0.95rem",
                  px: 2,
                  boxShadow: localCommissionType === 2 ? "0 4px 12px rgba(76, 175, 80, 0.3)" : "none",
                  transition: "all 0.3s ease",
                  "& .MuiChip-icon": {
                    color: localCommissionType === 2 ? "white" : "#4caf50"
                  },
                  "&:hover": {
                    transform: "translateY(-2px)",
                    background: localCommissionType === 2 
                      ? "linear-gradient(135deg, #5cb85c, #449d44)"
                      : "rgba(76, 175, 80, 0.15)",
                    boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)"
                  }
                }}
              />
            </Box>

            {/* Descripción del tipo seleccionado */}
            <Alert 
              icon={<Info />}
              severity="info" 
              sx={{ 
                backgroundColor: "rgba(76, 175, 80, 0.08)",
                color: "#2e7d32",
                border: "1px solid rgba(129, 199, 132, 0.5)",
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  color: "#4caf50"
                }
              }}
            >
              <AlertTitle sx={{ color: "#2e7d32", fontWeight: "bold" }}>
                {localCommissionType === 1 ? "Comisión Directa" : "Comisión Compartida"}
              </AlertTitle>
              {getCommissionTypeDescription(localCommissionType)}
            </Alert>
          </CardContent>
        </Card>

       {/* Campo de Comisión */}
      <Card sx={{ mb: 4, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                width: 40, 
                height: 40, 
                borderRadius: "50%", 
                background: "linear-gradient(135deg, #81c784, #66bb6a)",
                mr: 2
              }}
            >
              <Percent sx={{ fontSize: 24, color: "white" }} />
            </Box>
            <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: "bold" }}>
              Términos de Comisión
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            Selecciona los términos de distribución de comisión que prefieres:
          </Typography>

          <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap", justifyContent: "center" }}>
            <Chip
              icon={<Percent />}
              label="50/50"
              clickable
              onClick={() => handleCommissionChange("50/50")}
              sx={{
                height: 48,
                backgroundColor: localCommission === "50/50" 
                  ? "transparent"
                  : "rgba(76, 175, 80, 0.08)",
                background: localCommission === "50/50" 
                  ? "linear-gradient(135deg, #66bb6a, #4caf50)" 
                  : "rgba(76, 175, 80, 0.08)",
                color: localCommission === "50/50" ? "white" : "#2e7d32",
                border:localCommission === "50/50" ? "none" : "1px solid #81c784",
                fontWeight: localCommission === "50/50" ? "bold" : "medium",
                fontSize: "0.95rem",
                px: 2,
                boxShadow: localCommission === "50/50" ? "0 4px 12px rgba(76, 175, 80, 0.3)" : "none",
                transition: "all 0.3s ease",
                "& .MuiChip-icon": {
                  color: localCommission === "50/50" ? "white" : "#4caf50"
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  background: localCommission === "50/50" 
                    ? "linear-gradient(135deg, #5cb85c, #449d44)"
                    : "rgba(76, 175, 80, 0.15)",
                  boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)"
                }
              }}
            />
            
            <Chip
              icon={<Percent />}
              label="40/40/20"
              clickable
              onClick={() => handleCommissionChange("40/40/20")}
              sx={{
                height: 48,
                backgroundColor: localCommission === "40/40/20" 
                  ? "transparent"
                  : "rgba(76, 175, 80, 0.08)",
                background: localCommission === "40/40/20" 
                  ? "linear-gradient(135deg, #66bb6a, #4caf50)" 
                  : "rgba(76, 175, 80, 0.08)",
                color: localCommission === "40/40/20" ? "white" : "#2e7d32",
                border: localCommission === "40/40/20" ? "none" : "1px solid #81c784",
                fontWeight: localCommission=== "40/40/20" ? "bold" : "medium",
                fontSize: "0.95rem",
                px: 2,
                boxShadow: localCommission === "40/40/20" ? "0 4px 12px rgba(76, 175, 80, 0.3)" : "none",
                transition: "all 0.3s ease",
                "& .MuiChip-icon": {
                  color: localCommission === "40/40/20" ? "white" : "#4caf50"
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  background: localCommission === "40/40/20" 
                    ? "linear-gradient(135deg, #5cb85c, #449d44)"
                    : "rgba(76, 175, 80, 0.15)",
                  boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)"
                }
              }}
            />
          </Box>

          {/* Descripción de la opción seleccionada */}
          {localCommission && (
            <Alert 
              icon={<Info />}
              severity="info" 
              sx={{ 
                backgroundColor: "rgba(76, 175, 80, 0.08)",
                color: "#2e7d32",
                border: "1px solid rgba(129, 199, 132, 0.5)",
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  color: "#4caf50"
                }
              }}
            >
              <AlertTitle sx={{ color: "#2e7d32", fontWeight: "bold" }}>
                Distribución {localCommission}
              </AlertTitle>
              {localCommission=== "50/50" 
                ? "Repartición equitativa entre dos partes: 50% para cada broker involucrado en la transacción."
                : "Distribución entre tres partes: dos brokers reciben 40% cada uno y un tercero recibe 20%."
              }
            </Alert>
          )}

          {!localCommission && (
            <Box 
              sx={{ 
                p: 2, 
                textAlign: "center", 
                color: "text.secondary",
                backgroundColor: "rgba(0, 0, 0, 0.02)",
                borderRadius: 2,
                border: "1px dashed #ccc"
              }}
            >
              <Percent sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">
                Selecciona una opción de distribución de comisión
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      </Box>

      <PropertyFooterLayout
        progress={progress}
        step={step}
        onBack={handleBack}
        onContinue={handleContinue}
        isContinueDisabled={localCommission ? false: true}
      />
    </Container>
  )
}